<script setup lang="ts">
import { ref } from 'vue';
import SoundPlayer from '@/components/SoundPlayer.vue';
import NavItem from '@/components/NavItem.vue';
import Toggle from '@/components/ui/Toggle.vue';
import NotificationQueue from '@/components/NotificationQueue.vue';
import Modal from '@/components/ui/Modal.vue';
import UserCard from '@/components/UserCard.vue';
import { type User } from '@/stores/user';
import { updateTheme, loadTheme } from '@/theme-utils';
import type { PropType } from 'vue';

const props = defineProps({
    user: {
        type: Object as PropType<User>,
        required: false,
        default: undefined,
    },
});

const modal = ref<typeof Modal>();
let theme = ref(loadTheme());
</script>

<template>
    <nav>
        <div class="left-nav">
            <NavItem v-if="user" @click="modal?.open" tooltip="Settings">
                <UserCard :user="user" />
            </NavItem>
            <NavItem v-else href="/login/twitch/" is-django-route tooltip="Log in">
                <UserCard :user="user" />
            </NavItem>
            <NavItem v-if="user" href="/logout/" is-django-route icon="hi-logout" :iconScale="1.7" tooltip="Log out" />
        </div>

        <div class="middle-nav">
            <router-link class="nav-link" :class="{ active: $route.path === '/' }" to="/">Home</router-link>
            <router-link class="nav-link" :class="{ active: $route.path.startsWith('/games') }" to="/games">
                Games
            </router-link>
            <router-link class="nav-link" :class="{ active: $route.path === '/rules' }" to="/rules">
                Rules
            </router-link>
            <router-link class="nav-link" :class="{ active: $route.path === '/create-challenge' }"
                to="/create-challenge">
                Challenge Preferences
            </router-link>
            <router-link class="nav-link" :class="{ active: $route.path === '/ui-sandbox' }" to="/ui-sandbox">UI
                Sandbox</router-link>
            <!-- <NavItem href="/" icon="co-home" :iconScale="1.7" :active="$route.path === '/'" tooltip="Home" />
            <NavItem href="/games" icon="fa-gamepad" :iconScale="1.7" :active="$route.path === '/games'"
                tooltip="Games" />
            <NavItem href="/rules" icon="fa-info-circle" :iconScale="1.7" :active="$route.path === '/rules'"
                tooltip="Rules" />
            <NavItem href="/create-challenge" icon="co-plus" :icon-scale="1.7"
                :active="$route.path === '/create-challenge'" />
            <NavItem href="/ui-sandbox" icon="gi-soap-experiment" :iconScale="1.7"
                :active="$route.path === '/ui-sandbox'" tooltip="UI Sandbox" /> -->
        </div>

        <div class="right-nav">
            <Toggle :active="theme === 'dark'" @change="(value) => {
                theme = value ? 'dark' : 'light';
                updateTheme(theme);
            }" active-color="var(--color-blue)" inactive-color="var(--color-orange)" />
        </div>
    </nav>

    <div id="content">
        <main>
            <slot />
        </main>
        <footer class="credits" style="grid-area: footer;">
            <a href="https://www.twitch.tv/laserbelch" title="LaserBelch's Twitch" target="_blank">
                <img src="/images/laser-avatar.png" alt="LaserBelch" />
                twitch.tv/laserbelch
            </a>
        </footer>
    </div>

    <div id="notification-queue">
        <NotificationQueue />
    </div>

    <Modal ref="modal" blur style="color: white;">
        <div class="modal-body">
            <h1>Settings</h1>
            <div class="theme-controls">
                <h2>Dark Theme</h2>
                <Toggle :active="theme === 'dark'" @change="(value) => {
                    theme = value ? 'dark' : 'light';
                    updateTheme(theme);
                }" active-color="var(--color-blue)" inactive-color="var(--color-orange)" />
            </div>
            <div class="sound-controls">
                <h2>Sound</h2>
                <SoundPlayer />
            </div>
        </div>
    </Modal>
</template>

<style scoped>
#content {
    height: 100%;
    min-height: 100dvh;
    overflow-y: auto;
    padding: 3.5rem 0 0 0;
    display: flex;
    flex-direction: column;
}

.theme-controls {
    z-index: 10;
    color: black;
}

nav {
    position: fixed;
    top: 0;
    right: 0;
    left: 0;
    height: 3rem;
    background: var(--gradient-bg-gray);
    padding: 0 0.5rem;
    display: grid;
    grid-template-columns: 1fr auto 1fr;
    justify-content: space-between;
    align-items: center;
    margin: 0.5rem;
    border-radius: 0.5rem;
    border: 1px solid rgba(255, 255, 255, 0.2);
    z-index: 100;

    &>div {
        display: flex;
        width: 100%;
        align-items: center;
        gap: 1rem;
    }

    .nav-link {
        text-decoration: none;
        color: var(--text-light);
        font-weight: 700;
        font-size: 1rem;
        width: fit-content;
    }

    .nav-link.active {
        color: var(--text-color-accent-primary);
    }

    .nav-link:not(.active):hover {
        color: var(--text-color-accent-secondary);
    }
}

.left-nav {
    justify-content: flex-start;
}

.middle-nav {
    justify-content: center;
    gap: 2rem;
}

.right-nav {
    justify-content: flex-end;
}

main {
    flex-grow: 1;
    display: grid;
}

.logo {
    color: var(--color-orange);
    text-decoration: none;
    font-weight: 700;
    font-size: 0.75rem;
    text-align: center;
}

.logo span {
    width: 100%;
}

footer {
    display: flex;
    flex-direction: row;
    justify-content: center;
    align-items: center;
    gap: 1rem;
    font-size: 14px;
    letter-spacing: 1px;
    padding: 0.5rem;
    flex-wrap: wrap;
    margin: 0 2rem 1rem;
}

footer img {
    height: 2rem;
}

footer a {
    display: inline-block;
}

.modal-body {
    display: grid;
    place-items: center;
    width: 500px;
    height: fit-content;
    background: linear-gradient(313deg, var(--color-grey) 0%, rgba(73, 73, 98, 1) 100%);
    border-radius: 0.5rem;
    padding: 1rem;

    h1 {
        font-size: 2rem;
        margin-bottom: 1rem;
        color: white;
    }

    h2 {
        font-size: 1.5rem;
        margin-bottom: 0.5rem;
        color: white;
    }

    &>* {
        display: flex;
        flex-direction: row;
        gap: 1rem;
        align-items: center;
    }
}


#notification-queue {
    position: fixed;
    bottom: 1rem;
    right: 1rem;
    z-index: 1000;
}
</style>